/**
 * Settings page configuration.
 *
 * The notification and privacy maps are stored as open maps, so the v1 surface
 * is data-driven from here: add a key to a group below and it renders — no
 * component changes required. But the backend validates every key against its
 * own enum (`NotificationPreference`, `PrivacyPreference`) and 422s one it does
 * not know, so a new key lands on both sides together. Defaults mirror the
 * `StandardNotificationPreferences` / `StandardPrivacySettings` hints and the
 * agreed product constraints (friends-only profiles).
 *
 * Lives in `shared` so both the web Settings page and the mobile Settings screen
 * render the same groups/keys/defaults — the keys are the contract for the
 * backend's preference maps, so web and mobile must not drift.
 */
import type { Language, WorkoutDay } from '../types/auth';
import type { DashboardProfile } from '../types/dashboard.types';
import type { SelectOption } from '../utils/select-options';

/** Who a settings group/row applies to. `client`/`trainer` are role-gated. */
export type SettingsAudience = 'all' | 'client' | 'trainer';

/** A single boolean setting backed by a key in an open-ended preference map. */
export interface ToggleSettingDef {
  key: string;
  label: string;
  description?: string | undefined;
  defaultValue: boolean;
  /** Body-data toggles are flagged so the UI can mark them as sensitive. */
  sensitive?: boolean | undefined;
}

/** A titled group of toggle settings, optionally gated to a role. */
export interface SettingsToggleGroup {
  id: string;
  title: string;
  audience: SettingsAudience;
  /** A line saying why the group is shown — used for role-specific groups. */
  bannerText?: string | undefined;
  items: ToggleSettingDef[];
}

// =============================================================================
// NOTIFICATIONS — delivery masters (push, scheduled report email) + categories
// =============================================================================

export const NOTIFICATION_GROUPS: SettingsToggleGroup[] = [
  {
    id: 'delivery',
    title: 'Delivery',
    audience: 'all',
    items: [
      {
        key: 'push_notifications',
        label: 'Push notifications',
        description: 'Also alert me on my devices.',
        defaultValue: true,
      },
      {
        // The scheduled report emails' own opt-out. The categories below still
        // decide which reports exist; this decides whether any are emailed.
        key: 'email_reports',
        label: 'Email reports',
        description: 'Scheduled progress and goal reports, sent as PDFs.',
        defaultValue: true,
      },
    ],
  },
  {
    id: 'activity',
    title: 'Activity',
    audience: 'all',
    items: [
      {
        key: 'workout_reminders',
        label: 'Workout reminders',
        defaultValue: true,
      },
      {
        key: 'achievement_notifications',
        label: 'Achievements & PRs',
        defaultValue: true,
      },
      {
        key: 'goal_notifications',
        label: 'Goal milestones',
        description:
          'Milestones and weekly goal reports, plus a monthly goal report by email.',
        defaultValue: true,
      },
      {
        key: 'challenge_notifications',
        label: 'Challenges',
        defaultValue: true,
      },
      {
        key: 'progress_updates',
        label: 'Weekly progress',
        description:
          'Weekly summaries, plus a quarterly progress report by email.',
        defaultValue: true,
      },
      {
        key: 'social_notifications',
        label: 'Friends & reactions',
        defaultValue: true,
      },
    ],
  },
  {
    id: 'trainer-from',
    title: 'From your trainer',
    audience: 'client',
    items: [
      {
        key: 'trainer_feedback',
        label: 'Trainer feedback',
        description: 'When your trainer comments on a workout.',
        defaultValue: true,
      },
      {
        key: 'plan_updates',
        label: 'Plan assigned or updated',
        defaultValue: true,
      },
    ],
  },
  {
    id: 'coaching',
    title: 'Coaching',
    audience: 'trainer',
    bannerText: 'Shown because you coach clients',
    items: [
      {
        key: 'client_requests',
        label: 'New client requests',
        defaultValue: true,
      },
      {
        key: 'client_activity',
        label: 'Client activity',
        description: 'Completed or missed sessions.',
        defaultValue: true,
      },
    ],
  },
];

// =============================================================================
// PRIVACY — friends-only; body data is opt-in (off by default)
// =============================================================================

export const PRIVACY_GROUPS: SettingsToggleGroup[] = [
  {
    id: 'friends',
    title: 'Friends',
    audience: 'all',
    items: [
      {
        key: 'allow_friend_requests',
        label: 'Allow friend requests',
        description: 'People who know your exact username can add you.',
        defaultValue: true,
      },
    ],
  },
  {
    id: 'friends-see',
    title: 'What friends can see',
    audience: 'all',
    items: [
      {
        key: 'show_workout_activity',
        label: 'Workout activity',
        defaultValue: true,
      },
      {
        key: 'show_achievements',
        label: 'Achievements & PRs',
        defaultValue: true,
      },
      {
        key: 'show_progress_stats',
        label: 'Progress stats',
        defaultValue: true,
      },
      {
        key: 'show_on_leaderboards',
        label: 'Leaderboards',
        defaultValue: true,
      },
      {
        key: 'show_progress_photos',
        label: 'Progress photos',
        defaultValue: false,
        sensitive: true,
      },
      {
        key: 'show_measurements',
        label: 'Body measurements',
        defaultValue: false,
        sensitive: true,
      },
    ],
  },
  {
    id: 'trainer-see',
    title: 'What your trainer can see',
    audience: 'client',
    bannerText: 'Your connected trainer only — never your friends',
    items: [
      {
        key: 'trainer_view_measurements',
        label: 'Body measurements',
        defaultValue: true,
      },
      {
        key: 'trainer_view_progress_photos',
        label: 'Progress photos',
        description: 'Off until you share.',
        defaultValue: false,
      },
    ],
  },
  {
    id: 'trainer-presence',
    title: 'Trainer presence',
    audience: 'trainer',
    bannerText: 'Visible to people looking for a trainer',
    items: [
      {
        key: 'accepting_new_clients',
        label: 'Accepting new clients',
        defaultValue: true,
      },
      {
        key: 'public_trainer_profile',
        label: 'Public trainer profile',
        description: 'Show your credentials and bio to prospective clients.',
        defaultValue: true,
      },
    ],
  },
];

/**
 * The privacy group answering "who may reach me". The blocklist names its
 * exceptions, so the Privacy panel renders it straight after this group rather
 * than at the end — which splits the groups in two either side of it.
 */
export const PRIVACY_REACH_GROUP_ID = 'friends';

/** The groups above the blocklist: who may reach you in general. */
export const PRIVACY_REACH_GROUPS = PRIVACY_GROUPS.filter(
  (group) => group.id === PRIVACY_REACH_GROUP_ID
);

/** The groups below the blocklist: what friends and trainers can see. */
export const PRIVACY_VISIBILITY_GROUPS = PRIVACY_GROUPS.filter(
  (group) => group.id !== PRIVACY_REACH_GROUP_ID
);

// =============================================================================
// SELECT / PICKER OPTIONS
// =============================================================================

export const LANGUAGE_OPTIONS: ReadonlyArray<{
  value: Language;
  label: string;
}> = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
  { value: 'ru', label: 'Русский' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'zh', label: '中文' },
];

export const DASHBOARD_OPTIONS: ReadonlyArray<{
  value: DashboardProfile;
  label: string;
}> = [
  { value: 'default', label: 'Default' },
  { value: 'social', label: 'Social' },
  { value: 'analytics', label: 'Analytics' },
  { value: 'trainer', label: 'Trainer' },
];

/** FormSelect-compatible option list (value widened to string). */
export const LANGUAGE_SELECT_OPTIONS: SelectOption[] = LANGUAGE_OPTIONS.map(
  (o) => ({ value: o.value, label: o.label })
);

export const WEEKDAYS: ReadonlyArray<{
  key: WorkoutDay;
  short: string;
  label: string;
}> = [
  { key: 'monday', short: 'M', label: 'Monday' },
  { key: 'tuesday', short: 'T', label: 'Tuesday' },
  { key: 'wednesday', short: 'W', label: 'Wednesday' },
  { key: 'thursday', short: 'T', label: 'Thursday' },
  { key: 'friday', short: 'F', label: 'Friday' },
  { key: 'saturday', short: 'S', label: 'Saturday' },
  { key: 'sunday', short: 'S', label: 'Sunday' },
];
